<template>
  <div v-if="list && list.length" ref="storeCart" class="storeCart">
    <div v-if="list && list.length" class="storeCartList">
      <CartItem
        v-for="(item, index) in list"
        :key="item.cartId"
        :index="index"
        :locale="locale"
        :lang="lang"
        :item="item"
        :cooldown="cooldownItems && cooldownItems.cooldowns.includes(item.shopItemId)"
        @setDetailOption="setDetailOption"
        @delItem="delItem"
        @isValidItem="isValidItem" />
    </div>
    <div v-if="list && list.length" class="storeCartInfo notranslate">
      <div v-if="totalPrice" class="price">
        <span class="label">{{ locale.total }}</span>
        <span class="total">{{ priceFormat(totalPrice) }}</span>
      </div>
      <div class="btns">
        <button type="button" class="btn btnClose" @click.stop="close()">{{ locale.close }}</button>
        <button
          type="button"
          class="btn btnOrder"
          :style="getDefaultBtnStyle"
          :disabled="!isValid"
          @click.stop="setSubmit()">
          {{ locale.do_order }}
        </button>
      </div>
    </div>
    <div v-else>{{ locale.no_data }}</div>
    <div
      v-if="isBusy"
      tabindex="0"
      @click.stop
      @mousedown.stop
      @mouseup.stop
      @pointerdown.stop
      @pointerup.stop
      @touchstart.stop
      @touchend.stop
      @wheel.prevent
      @keydown.stop.prevent
      class="isBusy"></div>
  </div>
</template>

<script>
import regExp from '@/common/regExp'
import CartItem from './CartItem'
import Item from './Item.vue'

export default {
  name: 'shopCart',
  props: [
    'locale',
    'lang',
    'responsive',
    'shopPageId',
    'isPublic',
    'visible'
  ],
  components: {
    CartItem
  },
  computed: {
    isOpen () {
      return !!(this.visible && this.list && this.list.length)
    },
    getShopResultData () {
      return this.$store.getters['shop/getShopResultData']
    },
    getCartList () {
      const shopPageId = this.shopPageId
      const list = this.$store.getters['cart/getList'](shopPageId)
      return (list && list.filter(x => x.cartOta === this.cartOta)) || []
    },
    getDefaultBtnStyle () {
      return this.$store.getters['common/getDefaultBtnStyle']
    },
    list () {
      const shopPageId = this.shopPageId
      const list = this.$store.getters['cart/getList'](shopPageId)
      return (list && list.filter(x => x.cartOta === this.cartOta)) || []
    },
    totalPrice () {
      const list = Array.isArray(this.list) ? this.list : []
      return list.reduce((sum, item) => {
        const price = Number(item.totalPrice)
        return sum + (isNaN(price) ? 0 : price)
      }, 0)
    },
    // 현재 렌더 중인 아이템들 기준으로만 판단
    validAll () {
      const cartIds = (this.list || []).map(item => item.cartId)
      if (cartIds.length === 0) return false // 빈 장바구니면 주문불가
      // 아직 보고되지 않은 아이템은 true로 간주
      return cartIds.every(cartId => this.validityMap[cartId] === true)
    },
    isValid () {
      const isAuth = this.$store.getters['authentication/isAuth']
      const validAll = this.validAll
      const hasIgnoreCooldown = this.orderCooldown
        ? this.cooldownItems && this.cooldownItems.ignoreCooldowns && this.cooldownItems.ignoreCooldowns.length > 0
        : true
      return isAuth && validAll && hasIgnoreCooldown
    },
    // 주문대기 시간 체크
    orderCooldown () {
      const shopPageId = this.shopPageId
      const shopCatCode = this.$store.getters['shop/getShopCatCode'](shopPageId)
      const getWaitingOrder = this.$store.getters['shop/getWaitingOrder'](shopCatCode)
      return getWaitingOrder
    },
    // 주문대기 시간 체크
    cooldownItems () {
      const shopPageId = this.shopPageId
      const shopCatCode = this.$store.getters['shop/getShopCatCode'](shopPageId)
      const inCooldown = this.orderCooldown
      const isIgnored = (shopItemId) => {
        const isIgnoreItem = this.$store.getters['shop/getIgnoreWaitingItem'](shopItemId)
        return isIgnoreItem
      }
      const ids = (this.list || []).map(item => item.shopItemId)
      const ignoreCooldowns = ids.filter(isIgnored)
      const cooldowns = inCooldown
        ? ids.filter(id => !isIgnored(id)) // 쿨다운 중엔 무시되지 않은 것만 제한
        : [] // 쿨다운 없으면 제한 없음
      // 필요하면 timeLeftMs, inCooldown도 함께 반환 가능
      return inCooldown && { cooldowns, ignoreCooldowns }
    }
  },
  async created () {
    const ota = await this.$store.dispatch('authentication/getOtaKey')
    this.cartOta = ota
    this._bound = false
    if (this.list.length === 0) {
      this.$emit('noCartItems')
    }
  },
  mounted () {
    // 열림 상태 변화에 따라 전역 리스너를 붙였다/뗀다
    this.$watch(() => this.isOpen, (open) => {
      open ? this._bindGlobal() : this._unbindGlobal()
    }, { immediate: true })
  },
  beforeDestroy () {
    this._unbindGlobal()
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onVvResize)
    }
  },
  watch: {
    list: {
      deep: true,
      handler (list) {
        if (list.length === 0) {
          this.close()
        }
      }
    }
  },
  data () {
    return {
      isBusy: false,
      _vvhBase: 0,
      _ignoreScrollUntil: 0,
      _touchFromBackdrop: false,
      cartOta: '',
      validityMap: Object.create(null),
      readyDeleteCartId: []
    }
  },
  methods: {
    _bindGlobal () {
      if (this._bound) return
      this._vvhBase = window.visualViewport.height
      window.addEventListener('click', this.onClick, true)
      window.addEventListener('touchstart', this.onClick, { passive: false })
      window.addEventListener('touchend', this.onTouchEnd, { passive: false })
      window.addEventListener('scroll', this.onScroll, { passive: true })
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.onVvResize)
      }
      this._bound = true
    },
    _unbindGlobal () {
      if (!this._bound) return
      window.removeEventListener('click', this.onClick, true)
      window.removeEventListener('touchstart', this.onClick, { passive: false })
      window.removeEventListener('touchend', this.onTouchEnd, { passive: false })
      window.removeEventListener('scroll', this.onScroll, { passive: false })
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.onVvResize)
      }
      this._bound = false
    },
    isValidItem (cartId, isValid) {
      if (!Object.prototype.hasOwnProperty.call(this.validityMap, cartId)) {
        this.$set(this.validityMap, cartId, !!isValid)
      } else {
        this.validityMap[cartId] = !!isValid
      }
    },
    priceFormat (price) {
      const currency = this.$store.getters['shop/getShopCurrency'] || null
      return this.$store.getters['util/getPriceFormatter'](price, currency)
    },
    setDetailOption (shopItemId) {
      this.$emit('setDetailOption', shopItemId)
    },
    delItem (cartId) {
      this.$store.commit('cart/delList', cartId)
    },
    close (e) {
      this.$emit('close', e)
    },
    onScroll (e) {
      if (!this.isOpen) return
      // 최근에 무시하기로 한 기간이면 패스
      if (Date.now() < this._ignoreScrollUntil) return

      // 인풋/셀렉트에 포커스가 있으면 닫지 않기
      const ae = document.activeElement
      if (ae && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName)) return

      // iOS 키보드로 인한 viewport 축소 시 닫지 않기
      if (window.visualViewport && this._vvhBase) {
        const vv = window.visualViewport
        if (vv.height < this._vvhBase - 40) return
      }
      this.close()
    },
    // 키보드 열림/닫힘 동안은 스크롤로 닫지 않기
    onVvResize () {
      // 키보드가 올라오면 height가 기준보다 작아짐
      const vv = window.visualViewport
      const keyboardOpen = this._vvhBase && vv && (vv.height < this._vvhBase - 40) // 여유 40px
      this._ignoreScrollUntil = keyboardOpen ? Date.now() + 2000 : Date.now() + 150 // 안정 버퍼
    },
    onClick (e) {
      if (!this.isOpen || this.isBusy) return
      if (this.isBusy) e.preventDefault()
      // 텍스트노드 대비 (nodeType 3)
      const tgt = e.target && e.target.nodeType === 1
        ? e.target
        : e.target && e.target.parentElement
      // 내부면 무시
      const insideCart = (tgt && tgt.closest && tgt.closest('.storeCart'))
      if (insideCart) {
        // 내부에서 시작된 터치는 합성 클릭을 막기 위해 살짝 보정
        if (e.type === 'touchstart') {
          // 내부터치 직후 발생할 수 있는 scroll/click을 잠시 무시
          this._ignoreScrollUntil = Date.now() + 400
        }
        return
      }
      // iOS 합성 클릭 억제: 배경 직접 탭이면 기본동작 막아 중복 트리거 방지
      // if (e.type === 'touchstart') {
      //   e.preventDefault()
      //   this._ignoreScrollUntil = Date.now() + 400
      // }
      this.close(e)
    },

    onTouchEnd () {
      if (!this.isOpen) return
      // 터치 끝난 직후 약간의 버퍼 동안 스크롤 닫기 무시
      this._ignoreScrollUntil = Math.max(this._ignoreScrollUntil, Date.now() + 200)
    },
    // shop(product v2) items check billing
    async checkOrderStatus () {
      /**
       * 주문하려는 상품의 shopPageId별 빌링(포스결제)이 발생했는지 체크
       * 발생이 되었다면 해당 ota는 만료처리 되기 때문에
       * 새로운 ota로 주문까지 계속 진행해야 한다
       * 이 케이스는 pos연동시에만 관계가 있으므로 연동되지 않는 환경에서는 불필요함(무조건200ok)
       */
      const shopPageIds = [this.shopPageId] // store에서만 사용하는 장바구니 이므로 pageId는 한개임
      if (!shopPageIds) return true
      try {
        const res = await Promise.all(
          shopPageIds.map(data => this.$store.dispatch('order/checkOrderStatus', data))
        )
        const result = res.map(r => r.result || r.status)
        const reqOta = result.find(x => x.newOTAKey)
        const errorItem = result.find(e => e && e.code)
        const errors = errorItem && this.getShopResultData(errorItem.code || 500)
        if (errors) {
          if (errors === 'invalid_request' && errorItem && errorItem.reason && errorItem.reason.includes('ota')) {
            this.$emit('setToastMessage', this.locale.invalid_key, 5000)
            this.list.map(item => {
              const cartId = item.cartId
              this.delItem(cartId)
            })
            if (this.isPublic) {
              await this.$store.dispatch('authentication/getOtaKey')
              await this.$store.dispatch('guest/getUserInfo')
            } else {
              const otaKey = this.$store.state.authentication.k
              const space = this.$store.state.authentication.space
              this.$store.dispatch('authentication/validateOtaKey', {otaKey: otaKey, space: space})
            }
          } else {
            alert(`${this.locale['message_' + errors]}\n${errorItem.reason}`)
          }
          return false
        } else {
          return reqOta || true
        }
      } catch (error) {
        console.log(error)
        const errors = error && this.getShopResultData(error.code || 500)
        alert(`${errors}\n${errors.reason}`)
        return false
      }
    },
    // shop(product v2) items submit
    async submitShopItems () {
      const shopPageId = this.shopPageId
      const shopCatCode = this.$store.getters['shop/getShopCatCode'](shopPageId)
      /**
       * payload 아이템 데이터 중
       * 다국어 객체 찾아 번역에 사용하는 값만 남기고 삭제
       */
      const excludeLangs = this.$store.getters['util/excludeLangs']
      const deepFilter = (data) => {
        const filterLangs = /name|shopItemName/
        if (Array.isArray(data)) {
          return data.map(deepFilter)
        } else if (data && typeof data === 'object') {
          return Object.entries(data).reduce((acc, [key, value]) => {
            acc[key] = filterLangs.test(key) && typeof value === 'object'
              ? excludeLangs(value)
              : deepFilter(value)
            return acc
          }, {})
        }
        return data
      }
      const shopItems = this.list.filter(item => {
        const isCooldown = this.cooldownItems
        const ignoreCooldowns = isCooldown && isCooldown.ignoreCooldowns
        return isCooldown ? ignoreCooldowns.includes(item.shopItemId) : true
      }).map(item => {
        this.readyDeleteCartId.push(item.cartId)
        return item.payload
      }).map(x => {
        return {
          pageId: this.shopPageId,
          priceInfo: {
            gross: '0'
          },
          ...deepFilter(x)
        }
      })
      const submitData = {
        catCode: shopCatCode,
        payload: shopItems
      }
      try {
        const res = await this.$store.dispatch('order/setSubmit', { data: submitData }) // 추후 추가정보 입력시
        if (res && res.status === 200) {
        } else {
          const errorCode = res && res.result && res.result.code || 500
          const errorMessage = this.getShopResultData(errorCode)
          this.$emit('setToastMessage', this.locale[errorMessage], 5000)
        }
        return res
      } catch (e) {
        console.error('[submitShopItems error]', e)
        this.$store.commit('loading', false)
        return false
      }
    },
    async setSubmit () {
      this.$store.commit('loading', true)
      const checkOrderStatus = await this.checkOrderStatus()
      if (checkOrderStatus) {
        this.isBusy = true
        try {
          if (checkOrderStatus.newOTAKey) {
            await this.$store.dispatch('authentication/getOtaKey', true)
          }
          const submit = await this.submitShopItems()
          if (submit && submit.status === 200) {
            this.$emit('complete', this.locale.complete_order)
            this.readyDeleteCartId.map(cartId => this.delItem(cartId))
          }
        } finally {
          this.isBusy = false
        }
      }
      this.$store.commit('loading', false)
    }
  }
}
</script>
